import { useEffect, useMemo, useState } from "react"
import fp from "lodash/fp"
import { useSelector } from "react-redux"

import type { Tower } from "types/towers.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useLazyPowerlineTowersQuery } from "features/api"

interface UseAllPowerlineTowersProps {
  powerlineId: number | undefined
}

export const useAllPowerlineTowers = ({ powerlineId }: UseAllPowerlineTowersProps) => {
  const orgId = useSelector(selectCurrentOrgId)
  const [allTowers, setAllTowers] = useState<Tower[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(null)
  const [getPowerlineTowers] = useLazyPowerlineTowersQuery()

  useEffect(() => {
    if (!orgId || !powerlineId) {
      setAllTowers([])
      setLoading(false)
      setError(null)
      return
    }
    let isMounted = true
    const fetchAll = async () => {
      setLoading(true)
      setError(null)
      try {
        let cursor: string | undefined = undefined
        let results: Tower[] = []
        do {
          const { data, error } = await getPowerlineTowers(
            {
              id: Number(powerlineId),
              params: { org_id: Number(orgId), limit: 100, cursor },
            },
            true,
          )
          if (error) throw error
          if (data?.results) {
            results = results.concat(data.results)
          }
          cursor = data?.next
        } while (cursor)
        if (isMounted) {
          setAllTowers(results)
        }
      } catch (err) {
        if (isMounted) setError(err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchAll()
    return () => {
      isMounted = false
    }
  }, [orgId, powerlineId, getPowerlineTowers])

  const towersById = useMemo(() => {
    return fp.keyBy((t: Tower) => t.id, allTowers)
  }, [allTowers])

  return {
    towers: allTowers,
    towersById,
    isLoading: loading,
    isError: error,
  }
}
